/**
 * **What a command-menu row does** — `projects/token-command-menu.md` 2.3-2.5, the one map from a
 * {@link CommandMenuRow} onto the Phase 1 action model's handlers ({@link UnitActionModel}). The
 * menu view never calls a handler itself; it asks {@link commandMenuRowEffect} and runs what comes
 * back, so every row reaches the model's one handler. Commands (`boost`) go
 * through the model's `dispatchGesture(withLeadingActivation(...))`; intents reach the room's own
 * pre-activation fold. Nothing here builds a command or a `batch`.
 *
 * The effects, by what the player does next:
 * - `fire` — the row resolves at once (Boost, Hide, Disengage, Overcharge, Shut Down, Boot Up,
 *   Engage COMP/CON, Detect Mines, an NPC's Stabilize, a self-targeted granted row such as a Protocol).
 * - `target` — the row arms the board-wide aim ({@link commandMenuAimFor}: a Skirmish mount, Ram,
 *   Grapple, Improvised Attack, a Quick Tech option, a targeted granted row, and the Dismount and
 *   Eject cell picks) and the board enters targeting mode (`TargetingMode.tsx`); the next click on
 *   a legal target resolves it through the host's `fireAt`, or for a cell pick on a space through
 *   its `onCommitCell`. The menu stays open, slid away to its edge (2c.9). One aim board-wide: arming replaces any other.
 * - `chooser` — the row's chooser is hosted on its own card in the menu (3.2: Stabilize, Search,
 *   Self-Destruct, Prepare, Disarm Mine; a pilot's Mount, Jockey and Ride). It arms the chooser's aim ({@link commandMenuChooserFor}
 *   names the card while it is armed), and the card's commit button fires through the model's own
 *   handler (`fireStabilize`, `fireSearch`, `fireSelfDestruct`, `firePrepare`, `fireDisarmMine`,
 *   `fireMount`, `fireJockey`, `fireRide`).
 *
 * No row opens the sheet: every chooser is hosted here (3.2). The Barrage confirm is
 * the host's own, which commits its mounts to the fire card (`TokenCommandMenuHost`).
 */
import { isActor, isCombatOver, perUnitState, projectForDisplay, sideOf, type Combatant, type EncounterState } from "@lancer-vtt/domain";
import { wouldFoldActivation, type FoldViewer } from "./activationPreview";
import type { MovePreview, MovePreviewVerb } from "./boostRing";
import { COMMAND_MENU_CHOOSERS, JOCKEY_ROW_CHOICE, NPC_STABILIZE_ROW, UNARMED_STRIKE_ROW, type JockeyRowId, type CommandMenuChooserId, type FireCommit, type CommandMenuRow, type CommandMenuScreenId } from "./commandMenu";
import type { ActionDraft } from "./combatInspector";
import { resolveActionRiderChoices } from "./combatWindows/unitActions";
import { isMaskedCombatant } from "./disclosure";
import { grantedAimFor, type UnitActionModel } from "./combatWindows/useUnitActionModel";

export type CommandMenuRowEffect = { kind: "fire" | "target" | "chooser"; run: () => void } | { kind: "none" };

/** The slice of {@link UnitActionModel} the menu's rows reach. */
export type CommandMenuModel = Pick<
  UnitActionModel,
  | "views"
  | "grantedViews"
  | "backswingWeaponId"
  | "boost"
  | "hide"
  | "disengage"
  | "overcharge"
  | "shutDown"
  | "bootUp"
  | "engageCompCon"
  | "takeBackCompCon"
  | "detectMine"
  | "stabilizeNpc"
  | "fireGranted"
  | "releaseGrapple"
  | "breakGrapple"
  | "contestGrapple"
  | "reload"
  | "jockeyMenu"
  | "shakeOff"
  | "dropHeld"
>;

/** Core rows that resolve on the click; `row` is the one chosen (a Reload row names its weapon). */
const FIRE: Readonly<Record<string, (m: CommandMenuModel, row: CommandMenuRow) => void>> = {
  boost: (m) => {
    const view = m.views.find((v) => v.id === "boost");
    m.boost(resolveActionRiderChoices(view?.optInRiders ?? [], {}));
  },
  hide: (m) => m.hide(),
  disengage: (m) => m.disengage(),
  overcharge: (m) => m.overcharge(),
  shut_down: (m) => m.shutDown(),
  boot_up: (m) => m.bootUp(),
  engage_compcon: (m) => m.engageCompCon(),
  take_back_compcon: (m) => m.takeBackCompCon(),
  detect_mine: (m) => m.detectMine(),
  // An NPC's Stabilize: its choice is fixed, so the row fires (the tree names it apart from the
  // mech's chooser row, `NPC_STABILIZE_ROW`).
  [NPC_STABILIZE_ROW]: (m) => m.stabilizeNpc(),
  // The grapple follow-up (3.3), the root's top row while a grapple is held.
  grapple_release: (m) => m.releaseGrapple(),
  grapple_break: (m) => m.breakGrapple(),
  grapple_contest: (m) => m.contestGrapple(),
  // A pilot's Reload (`pilot-command-menu` Q5): the row's one weapon, whether the one-click row or
  // a row on the Reload card.
  reload: (m, row) => m.reload(row.weapons?.[0]?.key),
  // The Jockey follow-ups and Shake Off (Q6), and a pilot's Drop (Q7), each at the root's top.
  ...Object.fromEntries((Object.keys(JOCKEY_ROW_CHOICE) as JockeyRowId[]).map((id) => [id, (m: CommandMenuModel) => m.jockeyMenu(JOCKEY_ROW_CHOICE[id])])),
  shake_off: (m) => m.shakeOff(),
  drop_prepare: (m) => m.dropHeld(),
};

/**
 * The aim each hosted chooser arms (set rather than toggled, so a click never disarms a chooser
 * already armed).
 */
const CHOOSER_AIMS: Readonly<Record<CommandMenuChooserId, (attackerId: string) => ActionDraft>> = {
  stabilize: (attackerId) => ({ attackerId, mode: "stabilize" }),
  search: (attackerId) => ({ attackerId, mode: "search" }),
  self_destruct: (attackerId) => ({ attackerId, mode: "self_destruct" }),
  prepare: (attackerId) => ({ attackerId, mode: "prepare", readiedActionId: "", weaponId: "", triggerText: "" }),
  disarm_mine: (attackerId) => ({ attackerId, mode: "disarm_mine" }),
  mount: (attackerId) => ({ attackerId, mode: "mount" }),
  jockey: (attackerId) => ({ attackerId, mode: "jockey" }),
  ride: (attackerId) => ({ attackerId, mode: "ride" }),
};

const isChooserId = (id: string): id is CommandMenuChooserId => (COMMAND_MENU_CHOOSERS as readonly string[]).includes(id);

/**
 * The hosted chooser the armed `aim` belongs to on unit `unitId`, or `null`. The tree holds that
 * chooser's card while this is set, so the card survives a reopen and goes when the aim clears
 * (fired, cancelled, Esc).
 */
export function commandMenuChooserFor(aim: ActionDraft | null, unitId: string): CommandMenuChooserId | null {
  if (!aim || aim.attackerId !== unitId) return null;
  return isChooserId(aim.mode) ? aim.mode : null;
}

/**
 * The core rows that arm a bare board pick: the melee verbs' target (Ram's knock-back defaults off,
 * RAW "may also"), and Dismount's, Eject's and a riding pilot's Dismount (ally)'s landing space
 * (cell-pick aims, whose space the host's `onCommitCell` sends).
 */
const BOARD_AIMS: Readonly<Record<string, (attackerId: string) => ActionDraft>> = {
  ram: (attackerId) => ({ attackerId, mode: "ram", push: false }),
  grapple: (attackerId) => ({ attackerId, mode: "grapple" }),
  improvised_attack: (attackerId) => ({ attackerId, mode: "improvised_attack" }),
  dismount: (attackerId) => ({ attackerId, mode: "dismount" }),
  eject: (attackerId) => ({ attackerId, mode: "eject" }),
  ride_dismount: (attackerId) => ({ attackerId, mode: "ride_dismount" }),
  // A pilot's Release (Q7): the held action's target, picked on the board.
  release: (attackerId) => ({ attackerId, mode: "release" }),
};

/**
 * The board-wide aim choosing `row` on `screen` arms, or `null` when the row is not targeted. One
 * derivation for the click (the `target` effect) and the hover preview, so a hovered row paints
 * exactly the reach its click arms: a Skirmish mount's first fireable weapon (a live Backswing
 * reservation pins its own weapon instead), a melee verb, a Dismount / Eject landing pick, a Quick
 * Tech option, a targeted granted row (`grantedAimFor`, the model's own mapping).
 */
export function commandMenuAimFor(args: {
  row: CommandMenuRow;
  screen: CommandMenuScreenId;
  model: Pick<CommandMenuModel, "grantedViews" | "backswingWeaponId">;
  unitId: string;
}): ActionDraft | null {
  const { row, screen, model, unitId } = args;
  if (!row.available || row.kind === "group" || row.child !== undefined || !row.actionId) return null;
  // A fire card weapon (R7): its own shot. A Barrage aim carries no mounts here; the hover only washes.
  if (row.kind === "weapon") {
    return row.attack === "barrage"
      ? { attackerId: unitId, mode: "barrage", weaponId: row.actionId, profileId: "", accuracy: 0 }
      : { attackerId: unitId, mode: "skirmish", weaponId: row.actionId, profileId: "", accuracy: 0 };
  }
  if (row.kind === "mount") {
    if (screen !== "skirmish") return null;
    const weaponId = model.backswingWeaponId ?? row.weapons?.find((w) => w.state === "armable")?.key ?? "";
    return { attackerId: unitId, mode: "skirmish", weaponId, profileId: "", accuracy: 0 };
  }
  // A weaponless mech's unarmed strike: a Skirmish naming no weapon (the engine's fallback strike).
  if (row.actionId === UNARMED_STRIKE_ROW) return screen === "skirmish" ? { attackerId: unitId, mode: "skirmish", weaponId: "", profileId: "", accuracy: 0 } : null;
  if (row.kind === "option") {
    if (screen === "quick_tech") return { attackerId: unitId, mode: "quick_tech", optionId: row.actionId, accuracy: 0 };
    // A live Full Tech's next pick (3.3) continues the open lock: its own shot, nothing queued
    // behind it (`advanceFullTechQueue` clears the aim once it fires).
    if (screen === "full_tech" && row.continuation) return { attackerId: unitId, mode: "full_tech", optionId: row.actionId, accuracy: 0, queue: [] };
    return null;
  }
  const board = BOARD_AIMS[row.actionId];
  if (board) return board(unitId);
  const granted = model.grantedViews.find((v) => v.id === row.actionId);
  return granted ? grantedAimFor(unitId, granted) : null;
}

/**
 * The effect of choosing `row` on `screen`. Drilling rows (groups, and actions with a child
 * screen) and pick toggles are the view's own navigation and return `none`; so does any row that
 * is not available.
 */
export function commandMenuRowEffect(args: {
  row: CommandMenuRow;
  screen: CommandMenuScreenId;
  model: CommandMenuModel;
  unitId: string;
  setAim: (aim: ActionDraft | null) => void;
}): CommandMenuRowEffect {
  const { row, screen, model, unitId, setAim } = args;
  if (!row.available || row.kind === "group" || row.child !== undefined || !row.actionId) return { kind: "none" };
  const id = row.actionId;
  const granted = model.grantedViews.find((v) => v.id === id);
  const aim = commandMenuAimFor({ row, screen, model, unitId });
  // A granted row keeps its model handler (its fail-closed guards); the rest set the aim.
  if (aim) return { kind: "target", run: granted ? () => model.fireGranted(granted) : () => setAim(aim) };
  if (row.kind === "mount" || row.kind === "option") return { kind: "none" };
  const fire = FIRE[id];
  if (fire) return { kind: "fire", run: () => fire(model, row) };
  if (isChooserId(id)) {
    const chooserAim = CHOOSER_AIMS[id](unitId);
    return { kind: "chooser", run: () => setAim(chooserAim) };
  }
  if (granted) return { kind: "fire", run: () => model.fireGranted(granted) };
  return { kind: "none" };
}

/**
 * A pick screen's confirm. Full Tech's "Run both" runs in the menu: it arms the first pick's aim
 * (targeting mode) with the second queued behind it ({@link advanceFullTechQueue} arms it once the
 * first fires), through the same `full_tech` aim and `fireAt` intent path the NPC sheet's chooser uses.
 * The Barrage confirm is not here: the host commits its mounts to the fire card.
 */
export function commandMenuConfirmEffect(args: {
  screen: CommandMenuScreenId;
  picks: readonly string[];
  unitId: string;
  setAim: (aim: ActionDraft | null) => void;
}): CommandMenuRowEffect {
  const { screen, picks, unitId, setAim } = args;
  if (screen === "full_tech") {
    const [first, ...rest] = picks;
    if (!first) return { kind: "none" };
    return { kind: "target", run: () => setAim({ attackerId: unitId, mode: "full_tech", optionId: first, accuracy: 0, queue: rest }) };
  }
  return { kind: "none" };
}

/**
 * The next aim for a menu-armed Full Tech (`aim.queue` set by {@link commandMenuConfirmEffect}),
 * or `undefined` to leave the aim alone. `fireAt` re-arms a fired pick as `optionId: ""` and keeps
 * the queue, so an option-less aim with a queue means a pick just went out: arm the next queued
 * pick, or clear the aim when none is left (the Full Tech is done; the server closes its lock at
 * two picks). An NPC sheet's Full Tech carries no queue and is never touched.
 */
export function advanceFullTechQueue(aim: ActionDraft | null): ActionDraft | null | undefined {
  if (aim?.mode !== "full_tech" || aim.optionId !== "" || aim.queue === undefined) return undefined;
  const [next, ...rest] = aim.queue;
  return next ? { ...aim, optionId: next, accuracy: 0, queue: rest } : null;
}

/**
 * The docked command menu's state for `combatant` (`projects/token-command-menu.md` 2c, the
 * 2026-09-24 rulings), or `null` when the menu does not open on it at all.
 *
 * The menu opens on any placed actor (`isActor`: mech, NPC or pilot, `pilot-command-menu` 2.1) the
 * viewer controls (`canControl`, the GM-inclusive ownership read), at any time. It then either **enables** its rows, or opens them **explorable** and
 * disabled, with one reason the tree says once, as the unit line under the header
 * (`commandMenuTree`'s `unitLine`, `projects/menu-advisory-diet.md` R2); its rows stay silent. A
 * DONE unit's menu is compact: the line, its reaction, no rows (F1).
 *
 * | the unit | state | headline | reason |
 * |---|---|---|---|
 * | acting | enabled | YOUR TURN | |
 * | ready (the viewer's first action starts its turn; the GM's does on any unit, 2026-09-28) | enabled | READY | |
 * | another unit is acting | explore | WAITING | <that unit>'s turn |
 * | nobody acting, the other side is picking | explore | WAITING | Enemy side's pick |
 * | already took its turn this round | explore | DONE | Acts again next round |
 * | on the picking side but unable to act | explore | WAITING | Can't act right now |
 *
 * "Would start its turn" is {@link wouldFoldActivation}, the room's own fold test, so an enabled
 * `READY` menu never promises a costed turn the room won't open.
 *
 * Control is necessary but not sufficient: the viewer must also receive the unit unmasked
 * (`projectForDisplay`). A player holding a control grant without the visibility grant on a
 * hostile-side mech gets no menu (`decisions/0179`: control without EYES cannot fire), the same
 * rule the NPC inspector's `mountsNpcControl` applies. So the menu never renders masked data.
 */
export type CommandMenuState =
  | { kind: "enabled"; headline: "YOUR TURN" | "READY" | "OVERRIDE" }
  | { kind: "explore"; headline: "WAITING" | "DONE"; reason: string };

/** The unit line while another unit is acting: whose turn it is, which the WAITING word can't say. */
export function otherActingReason(actorName: string): string {
  return `${actorName}'s turn`;
}
export const MENU_REASON_ENEMY_PICK = "Enemy side's pick";
export const MENU_REASON_DONE = "Acts again next round";
export const MENU_REASON_UNABLE = "Can't act right now";

export function commandMenuStateFor(args: {
  combatant: Combatant;
  state: EncounterState;
  canControl: boolean;
  viewer: FoldViewer;
}): CommandMenuState | null {
  const { combatant: c, state, canControl, viewer } = args;
  // A finished fight (`end_combat`) offers no actions: the server refuses them all.
  if (isCombatOver(state) || !canControl || !isActor(c) || c.pos == null) return null;
  if (isMaskedCombatant(projectForDisplay({ combatant: c, viewerSlot: viewer.actorId ?? null, role: viewer.isGm ? "gm" : "player", state }))) return null;
  const unit = perUnitState(state)[c.id];
  if (unit === undefined) return null;
  if (unit === "active") return { kind: "enabled", headline: "YOUR TURN" };
  if (unit === "activated") return { kind: "explore", headline: "DONE", reason: MENU_REASON_DONE };
  // Every viewer who controls a ready unit folds its activation with an action (`controlsActivation`).
  if (unit === "ready" && wouldFoldActivation(state, c, viewer)) return { kind: "enabled", headline: "READY" };
  // `on_deck`: someone else is mid-turn, the other side is picking, or the unit can't act.
  const turn = state.activation.turn;
  if (turn.phase === "acting") return { kind: "explore", headline: "WAITING", reason: otherActingReason(state.combatants[turn.actor]?.name ?? "Another unit") };
  if (sideOf(c) !== state.activation.activeSide) return { kind: "explore", headline: "WAITING", reason: MENU_REASON_ENEMY_PICK };
  return { kind: "explore", headline: "WAITING", reason: MENU_REASON_UNABLE };
}

/**
 * The aim a hovered row previews on the board (2.4, 2c.5), or `null` for a row with no reach.
 * **Every row with a reach previews it, at any depth, whatever its availability or the menu's
 * state** (range-hover, developer 2026-09-28: "any time I hover any weapon or ability in any menu
 * at any depth, its range zone should show up"): a used, unloaded or unaffordable row, and an
 * explore menu's row, show the reach they would have. A hover preview never arms; the click on a
 * greyed row stays inert (`commandMenuRowEffect`). Off-turn the board computes the reach over the
 * unit's `moveStateFor` state (`useTargetingWashes`' `washState`), a fresh turn where it stands.
 *
 * What previews what:
 * - a Skirmish or Barrage mount row: the weapon under the pointer (`weaponKey`), else its first
 *   fireable weapon, else its first (a Backswing reservation on the Skirmish card pins its own);
 * - a fire card weapon, a Reload row: that weapon;
 * - Ram, Grapple, Improvised Attack and a pilot's Mount, Jockey, Ride: their adjacent reach;
 * - a Quick Tech or Full Tech option: Sensors;
 * - a targeted granted row (`grantedAimFor`): its frozen reach, or Sensors for a tech attack;
 * - a drilling row, a self-only row (Boost, Hide, a self-targeted system): nothing. Boost and
 *   Disengage paint their move instead ({@link commandMenuPreviewMove}).
 */
export function commandMenuPreviewAim(args: { row: CommandMenuRow; screen: CommandMenuScreenId; model: CommandMenuModel; unitId: string; weaponKey?: string | undefined }): ActionDraft | null {
  const { row, screen, unitId, weaponKey } = args;
  if (row.child) return null;
  const open = { ...args, row: { ...row, available: true } };
  if (row.kind === "mount") {
    const weaponId = (weaponKey !== undefined && row.weapons?.some((w) => w.key === weaponKey) ? weaponKey : undefined) ?? (screen === "skirmish" ? args.model.backswingWeaponId : undefined) ?? row.weapons?.find((w) => w.state === "armable")?.key ?? row.weapons?.[0]?.key;
    if (weaponId === undefined) return null;
    return screen === "barrage" ? { attackerId: unitId, mode: "barrage", weaponId, profileId: "", accuracy: 0 } : { attackerId: unitId, mode: "skirmish", weaponId, profileId: "", accuracy: 0 };
  }
  if (row.actionId === "reload" && row.weapons?.[0]) return { attackerId: unitId, mode: "skirmish", weaponId: row.weapons[0].key, profileId: "", accuracy: 0 };
  // Full Tech's picks toggle rather than arm, so the click's aim is nothing; each previews its own shot.
  if (row.kind === "option" && screen === "full_tech" && row.actionId) return { attackerId: unitId, mode: "full_tech", optionId: row.actionId, accuracy: 0, queue: [] };
  const id = row.actionId;
  if (id && isChooserId(id) && ADJACENT_PREVIEW.has(id)) return CHOOSER_AIMS[id](unitId);
  return commandMenuAimFor(open);
}

/** The chooser rows whose hover paints the adjacent reach their aim washes (a pilot's Mount, Jockey and Ride, 2.4). */
const ADJACENT_PREVIEW: ReadonlySet<CommandMenuChooserId> = new Set(["mount", "jockey", "ride"]);

/** The movement rows whose hover previews the cells the move would reach (Phase 3). */
const MOVE_PREVIEW: Readonly<Record<string, MovePreviewVerb>> = { boost: "boost", disengage: "disengage" };

/**
 * The movement preview a hovered row paints (Boost, Disengage), or `null`: the movement-kind twin
 * of {@link commandMenuPreviewAim}. An enabled menu previews a row it could take; an explore menu, a
 * row greyed by the unit wall alone (`blockedBy: "unit"`, R8), which is the move it would have. The
 * board computes the reach over the unit's `moveStateFor` state (`moveVerbReach`), so an off-turn
 * hover shows a fresh turn's move from where the unit stands.
 */
export function commandMenuPreviewMove(args: { row: CommandMenuRow; unitId: string; menuState: CommandMenuState }): MovePreview | null {
  const { row, unitId, menuState } = args;
  const verb = row.kind === "action" && row.actionId ? MOVE_PREVIEW[row.actionId] : undefined;
  if (!verb) return null;
  const previewable = menuState.kind === "enabled" ? row.available : row.blockedBy === "unit";
  return previewable ? { unitId, verb } : null;
}

/**
 * **The unit the docked menu shows: the selection, and nothing else** (dev feedback, 2026-09-24).
 * The selected unit when {@link commandMenuStateFor} admits it, with that state; otherwise `null`,
 * so selecting a unit the menu does not open on (an enemy, an object) or deselecting hides it. There
 * is no second source (no "clicked" unit, no active-unit fallback), which is what keeps the menu
 * from drifting off the selection.
 */
export function commandMenuUnitFor(args: {
  state: EncounterState;
  selectedId: string | null;
  stateOf: (c: Combatant) => CommandMenuState | null;
}): { unit: Combatant; menuState: CommandMenuState } | null {
  const { state, selectedId, stateOf } = args;
  const unit = selectedId ? state.combatants[selectedId] : undefined;
  const menuState = unit ? stateOf(unit) : null;
  return unit && menuState ? { unit, menuState } : null;
}

/**
 * Whom to select when the acting unit changes, so the menu shows the active unit unasked (Phase
 * 2.3) through the one selection: the new actor, when the menu may show it and nothing is selected.
 * `null` leaves the selection alone (another unit is selected, the turn ended, or the menu does not
 * open on the actor).
 */
export function selectOnTurnStart(args: { actorId: string | null; selectedId: string | null; mayShow: (id: string) => boolean }): string | null {
  const { actorId, selectedId, mayShow } = args;
  return actorId !== null && selectedId === null && mayShow(actorId) ? actorId : null;
}

/**
 * Whether the menu's local fire commitment is spent without a lock (a one-weapon Skirmish, a
 * Superheavy Barrage): its action reads used. A live Backswing Cut reservation re-lights a used
 * Skirmish, so a Skirmish commitment made for the follow-up is not spent until the reservation is
 * (the host then drops it and the fire card goes).
 */
export function fireCommitSpent(args: { commit: FireCommit | null; model: Pick<CommandMenuModel, "views" | "backswingWeaponId">; actionUses?: Readonly<Record<string, number>> | undefined }): boolean {
  const { commit, model, actionUses } = args;
  if (!commit) return false;
  // A pilot's Fight: spent once its ledger count has moved since the commit.
  if (commit.ledger) return (actionUses?.[commit.ledger.key] ?? 0) > commit.ledger.at;
  if (commit.action === "skirmish" && model.backswingWeaponId !== undefined) return false;
  return model.views.find((v) => v.id === commit.action)?.usedUp ?? false;
}

/**
 * Whether `attacker` can take an action for this viewer right now: the viewer controls it and its
 * turn is open, or it is `ready` and the pre-activation fold would open it (the menu's `enabled`
 * states, `commandMenuStateFor`, for any unit kind).
 */
export function aimAttackerCanAct(args: { state: EncounterState; attacker: Combatant; canControl: boolean; viewer: FoldViewer }): boolean {
  const { state, attacker, canControl, viewer } = args;
  if (!canControl) return false;
  const unit = perUnitState(state)[attacker.id];
  return unit === "active" || (unit === "ready" && wouldFoldActivation(state, attacker, viewer));
}

/** What {@link staleAim} remembers between renders: the armed aim's attacker and whether it could act. */
export type AimActSeen = { attackerId: string; canAct: boolean };

/**
 * Whether the armed `aim` went stale: its attacker could act when last seen and now cannot (another
 * unit became the acting one, its own turn ended, or it left the viewer's control). The host then
 * clears the aim, as arming is disabled off-turn. An aim armed while its attacker already could not
 * act is left alone, and so are the two aims that are off-turn by design: a reaction (an Overwatch
 * take or an NPC's declared Overwatch) and a held action's release.
 */
export function staleAim(args: { aim: ActionDraft | null; seen: AimActSeen | null; canAct: boolean }): boolean {
  const { aim, seen, canAct } = args;
  if (!aim || aim.mode === "release" || (aim.mode === "skirmish" && aim.reaction === true)) return false;
  return seen !== null && seen.attackerId === aim.attackerId && seen.canAct && !canAct;
}
